import * as THREE from 'three';
import { SplatMesh, SparkRenderer } from '@sparkjsdev/spark';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { PaperShader } from './paper-shader.ts';
import { PaperSprite } from './paper-sprite.ts';
import { CHARACTER_LAYOUT, PROPS, type VoiceKey } from './script.ts';
import { fxSettings } from './settings.ts';

export type View = 'audience' | 'backstage';

const SPLAT_URL = '/splats/Abandoned%20Theater%20Interior.spz';

/** Paper props live on this layer so they bypass the stylization pass and
 *  render unfiltered on top of the post-processed splat. */
const PAPER_LAYER = 1;

/** Vertical shift applied to all character sprites and props at spawn time —
 *  positions the "ground" so the cast stands at the right height relative
 *  to the floor overlay. Audience camera covers ~4.85 world units
 *  vertically (FOV 60° at distance 4.2) ≈ 100vh, so 1vh ≈ 0.0485 world
 *  units. Curtains, backdrop, and masks ignore this. */
const GROUND_Y_OFFSET = -0.76;

/** Default transform for the theater splat. Auto-fit overrides position/scale on load. */
const SPLAT_DEFAULT = {
  position: new THREE.Vector3(0, 0, 0),
  rotationEuler: new THREE.Euler(0, 0, 0),
  scale: 1.0,
};

type CameraPose = { pos: THREE.Vector3; look: THREE.Vector3; fov: number };

const POSES: Record<View, CameraPose> = {
  // Audience: looking at the proscenium head-on, slightly low. Wider FOV than
  // the original 38° so the surrounding splatted theater reads as volume,
  // not as a flat background behind a tunnel.
  audience: {
    pos: new THREE.Vector3(0, 0.1, 4.2),
    look: new THREE.Vector3(0, 0.1, 0),
    fov: 60,
  },
  // Backstage: over the puppeteer's shoulder, looking at the back of the stage.
  backstage: {
    pos: new THREE.Vector3(0.6, 1.1, -3.6),
    look: new THREE.Vector3(0, 0.2, 0),
    fov: 70,
  },
};

export class Stage {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  /** Background renderer — drives the #stage-bg canvas. Renders the
   *  splat / backdrop / puppeteer silhouette on layer 0, sits BEHIND
   *  the floor HTML overlay. */
  private bgRenderer!: THREE.WebGLRenderer;
  readonly renderer: THREE.WebGLRenderer;
  private composer!: EffectComposer;
  private paperPass!: ShaderPass;

  private spotlight: THREE.SpotLight;

  private puppetSlot: THREE.Mesh;
  private maskShelf: THREE.Group;
  private puppeteerSilhouette: THREE.Mesh;
  /** Textured puppeteer figure rendered behind the floor on the bg
   *  canvas. Hidden by default — only shown while the play is awaiting
   *  the player's voice (voice_prompt or conversation beat). Animated:
   *  slides up from below the stage on show, slides back down on hide. */
  private puppeteerMesh!: THREE.Mesh;
  /** Y position when fully on stage (face above the floor strip). */
  private static readonly PUPPETEER_HOME_Y = -0.2;
  /** Y position when fully tucked below the stage (out of camera view). */
  private static readonly PUPPETEER_HIDDEN_Y = -5.0;
  private puppeteerTargetY = Stage.PUPPETEER_HIDDEN_Y;

  /** Paper-character sprites keyed by VoiceKey. Hidden until an `enter` beat. */
  private sprites = new Map<VoiceKey, PaperSprite>();
  /** Scene-element prop sprites keyed by id (tree, castle, ...). Hidden until a `prop` beat. */
  private props = new Map<string, PaperSprite>();

  /** Click hotspots active during a choice beat: target id → callback. */
  private hotspotHandlers = new Map<string, () => void>();
  private raycaster = new THREE.Raycaster();
  private pointerVec = new THREE.Vector2();

  /** Painted backdrop behind the action; texture swapped via setBackdrop(). */
  private backdrop!: THREE.Mesh;
  private backdropTextures = new Map<string, THREE.CanvasTexture>();
  /** Loaded /img/scene.webp — the universal backdrop until per-set art arrives. */
  private sceneTexture: THREE.Texture | null = null;

  /** Animated curtain — full-screen <img> overlay cycling through 121
   *  frames in /sprites/b_papercraft_video_ani/frames_nobg. The frame
   *  set encodes a single forward-play loop: open (frame 1) → closed
   *  (frame 61) → open (frame 121). Frames 1 and 121 are visually
   *  identical "open" states; we snap between them invisibly so each
   *  open/close call can play forward through its half of the loop. */
  private curtainImgEl: HTMLImageElement | null = null;
  private curtainFramesPreloaded: HTMLImageElement[] = [];
  private curtainFrameIndex = 60; // 0-indexed; 60 = 1-indexed frame 61 = CLOSED
  private static readonly CURTAIN_FRAME_COUNT = 121;
  private static readonly CURTAIN_CLOSED_FRAME_1 = 61;   // 1-indexed midpoint = closed
  private static readonly CURTAIN_OPEN_START_1 = 1;      // 1-indexed first frame = open
  private static readonly CURTAIN_OPEN_END_1 = 121;      // 1-indexed last frame = open
  private static readonly CURTAIN_DURATION_MS = 1800;
  /** Resolves once every curtain frame PNG has finished loading.
   *  curtainOpen / curtainClose await this so they never animate
   *  through frames that haven't loaded yet (would show as a flash). */
  private curtainPreloadPromise: Promise<void> = Promise.resolve();

  /** Just the paper frame (curtains + valance). Toggled with P; hidden by default. */
  private proscenium!: THREE.Group;

  /** Photoreal theater interior, behind the paper proscenium. Loaded asynchronously. */
  splat: SplatMesh | null = null;
  private splatReady: Promise<void> | null = null;

  private currentView: View = 'audience';
  private viewLerp = 1;
  private viewLerpDuration = 1000;
  private viewLerpStart = 0;
  private fromPose: CameraPose;
  private toPose: CameraPose;
  // Mouse-driven camera parallax. Target is set by main.ts on mousemove;
  // applied is the smoothed value currently summed onto camera.position.
  // We strip & re-apply each frame so view lerps and snapshots see the
  // unbiased base position.
  private parallaxTargetX = 0;
  private parallaxTargetY = 0;
  private parallaxAppliedX = 0;
  private parallaxAppliedY = 0;
  private currentLook = new THREE.Vector3(0, 0.1, 0);

  private rotateSplatEnabled = false;
  private cameraCutsEnabled = false;
  private splatVisibleEnabled = false;
  private lastFrameTime = performance.now();

  constructor(canvas: HTMLCanvasElement) {
    // Two canvases for fg/bg compositing around the floor strip:
    //   bgRenderer (#stage-bg, z-1) → splat + backdrop + silhouette
    //                                  (rendered behind the HTML floor)
    //   renderer   (#stage,    z-3) → paper layer (sprites, props, etc.)
    //                                  (rendered in front of the floor)
    // Both use alpha:true + clearAlpha 0 so transparent regions composite
    // cleanly with the floor / understage HTML between them.
    const bgCanvas = document.getElementById('stage-bg') as HTMLCanvasElement | null;
    if (!bgCanvas) throw new Error('#stage-bg canvas missing');
    this.bgRenderer = new THREE.WebGLRenderer({ canvas: bgCanvas, antialias: false, alpha: true });
    // BG keeps PR=1 because the splat is fragment-bound; doubling the
    // pixel count tanks framerate. The splat & backdrop are blurry-by-
    // intent (cool aerial perspective).
    this.bgRenderer.setPixelRatio(1);
    this.bgRenderer.setClearColor(0x000000, 0);
    this.bgRenderer.outputColorSpace = THREE.SRGBColorSpace;

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    // FG renders at the device's native pixel ratio (capped at 2) so
    // sprites stay crisp on Retina / HiDPI displays. With PR=1, the
    // GPU samples the texture once per CSS pixel and the browser
    // upscales by the device ratio — that's the blur the user sees on
    // big sprites like the witch house.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    this.applyPose(POSES.audience);
    this.fromPose = POSES.audience;
    this.toPose = POSES.audience;

    // Spark needs a SparkRenderer in the scene to actually rasterize SplatMesh
    // objects — Three.js calls its onBeforeRender hook once per frame. Tuned
    // for room-scale: clamp Gaussian extent + max pixel radius to cut overdraw.
    // Bound to the BACKGROUND renderer (#stage-bg) since that's where the
    // splat is rasterized.
    const spark = new SparkRenderer({
      renderer: this.bgRenderer,
      maxStdDev: Math.sqrt(4),
      maxPixelRadius: 192,
      minPixelRadius: 1.5,
    });
    this.scene.add(spark);

    // Light fog only — the splat is the world now, so we can't fade it to black.
    this.scene.fog = new THREE.FogExp2(0x000000, 0.04);

    // Ambient — barely there. Affects paper layer too so the props are lit
    // when we render them on top of the stylized splat.
    const ambient = new THREE.AmbientLight(0x2a1a10, 0.25);
    ambient.layers.enable(PAPER_LAYER);
    this.scene.add(ambient);

    // Stage spotlight (footlights feel) — warm, narrow, from above-front.
    this.spotlight = new THREE.SpotLight(0xffd9a3, 2.4, 12, Math.PI / 6, 0.6, 1.2);
    this.spotlight.position.set(0, 3.2, 2.5);
    this.spotlight.target.position.set(0, 0, 0);
    this.spotlight.layers.enable(PAPER_LAYER);
    this.scene.add(this.spotlight);
    this.scene.add(this.spotlight.target);

    // Stage floor: now rendered as an HTML overlay (#floor-overlay,
    // floor.png) instead of a 3D plane, so the baked-in perspective in
    // the source image is what the audience sees.

// Proscenium — dark frame around the play area. Two side curtains, top valance.
    // Hidden by default; toggle with stage.toggleProscenium() / P key.
    this.proscenium = new THREE.Group();
    this.proscenium.visible = false;
    this.scene.add(this.proscenium);
    const curtainMat = new THREE.MeshStandardMaterial({ color: 0x07050a, roughness: 1 });
    const left = new THREE.Mesh(new THREE.PlaneGeometry(1.4, 3.6), curtainMat);
    left.position.set(-2.6, 0.2, -0.8);
    this.proscenium.add(left);
    const right = left.clone();
    right.position.x = 2.6;
    this.proscenium.add(right);
    const top = new THREE.Mesh(new THREE.PlaneGeometry(6, 1.0), curtainMat);
    top.position.set(0, 1.6, -0.8);
    this.proscenium.add(top);

    // Puppet slot — a placeholder paper cutout. Replaced per-scene.
    this.puppetSlot = new THREE.Mesh(
      new THREE.PlaneGeometry(0.7, 1.1),
      new THREE.MeshStandardMaterial({
        color: 0xc8b48c,
        roughness: 1,
        transparent: true,
        side: THREE.DoubleSide,
      })
    );
    this.puppetSlot.position.set(0, -0.05, -0.4);
    this.puppetSlot.visible = false;
    this.scene.add(this.puppetSlot);

    // Puppeteer silhouette — visible faintly behind the backdrop in audience view.
    this.puppeteerSilhouette = new THREE.Mesh(
      new THREE.PlaneGeometry(1.4, 1.6),
      new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.6 })
    );
    this.puppeteerSilhouette.position.set(0, 0.6, -1.55);
    this.puppeteerSilhouette.visible = false;
    this.scene.add(this.puppeteerSilhouette);

    // Mask shelf — floats above the action so the placeholder squares (and
    // the active lifted one) read clearly against the backdrop. Originally
    // at y=0.05 ("low backstage shelf"); raised here so it's not occluded by
    // the speaking sprites.
    this.maskShelf = new THREE.Group();
    this.maskShelf.position.set(0, 1.0, 0.2);
    const maskColors = [0xc9a77c, 0x6e3a2c, 0xa8b8c4, 0x4a3a5e, 0xd1c08a];
    maskColors.forEach((color, i) => {
      const mask = new THREE.Mesh(
        new THREE.PlaneGeometry(0.32, 0.42),
        new THREE.MeshStandardMaterial({ color, roughness: 1, side: THREE.DoubleSide })
      );
      mask.position.set(-0.9 + i * 0.45, 0, 0);
      mask.userData.baseY = 0;
      this.maskShelf.add(mask);
    });
    this.maskShelf.visible = fxSettings.showMasks;   // hidden by default
    this.scene.add(this.maskShelf);

    // Move all paper props onto PAPER_LAYER so the stylize pass skips them
    // and we render them on a second, unfiltered pass on top.
    // Note: puppeteerSilhouette + backdrop stay on layer 0 — they're the
    // BACKGROUND, rendered to #stage-bg behind the floor HTML overlay.
    this.proscenium.traverse(o => o.layers.set(PAPER_LAYER));
    this.puppetSlot.layers.set(PAPER_LAYER);
    this.maskShelf.traverse(o => o.layers.set(PAPER_LAYER));

    // Spawn one PaperSprite per character. Hidden until `enter` beats fire.
    // PaperSprite's default homeY is (h/2 - 0.7) ≈ -0.025 for the default
    // 1.35 height; shift by GROUND_Y_OFFSET so the cast stands lower.
    for (const [voice, layout] of Object.entries(CHARACTER_LAYOUT) as [VoiceKey, typeof CHARACTER_LAYOUT[VoiceKey]][]) {
      const sprite = new PaperSprite({
        id: voice,
        label: layout.label,
        color: layout.color,
        homeX: layout.homeX,
        homeY: -0.025 + GROUND_Y_OFFSET,
        imageUrl: layout.imageUrl,
        altImageUrl: layout.altImageUrl,
        fit: layout.fit,
        width: layout.width,
        height: layout.height,
        flipX: layout.flipX,
        idle: layout.idle,
      });
      sprite.setLayer(PAPER_LAYER);
      this.scene.add(sprite.mesh);
      this.sprites.set(voice, sprite);
    }

    // Spawn one PaperSprite per scene-element prop. Z is pushed slightly behind
    // the characters so they overlap correctly when a character stands in front.
    for (const [key, def] of Object.entries(PROPS)) {
      const prop = new PaperSprite({
        id: key,
        label: def.label,
        color: def.color,
        homeX: def.x,
        homeY: (def.y ?? 0) + GROUND_Y_OFFSET,
        homeZ: def.z ?? -0.7,
        width: def.width,
        height: def.height,
        imageUrl: def.imageUrl,
        fit: def.fit,
        invisible: def.invisible,
        // Environmental props (trees, rocks, castles, altars) stay still.
        // They don't levitate. Characters keep their breath.
        idle: false,
      });
      prop.setLayer(PAPER_LAYER);
      this.scene.add(prop.mesh);
      this.props.set(key, prop);
    }

    // Painted set backdrop — fills the stage opening, swaps via setBackdrop().
    // Stays on layer 0 (background) so it renders to #stage-bg (z-1)
    // BEHIND the floor HTML overlay — never covers the floor strip.
    this.backdrop = new THREE.Mesh(
      new THREE.PlaneGeometry(10, 6),
      // color is multiplied with the texture map. Cool blue cast at
      // ~21% brightness — pushes the backdrop into the distance via
      // aerial perspective (cool/far, warm/near). Cast stays neutral
      // gray, the front curtain is tinted warm in CSS.
      new THREE.MeshBasicMaterial({
        side: THREE.DoubleSide,
        color: 0x2a3852,
      }),
    );
    this.backdrop.position.set(0, 0.1, -1.0);
    this.scene.add(this.backdrop);
    this.setBackdrop('theater');

    // Async-load the universal backdrop. /img/Paper Game Image.png is
    // the default for every set key that doesn't have its own override
    // (woods, theater, clearing, etc.). The original scene.webp lives
    // in BACKDROP_OVERRIDES under finale_warm / finale_cold so the
    // ending scenes keep their original look.
    new THREE.TextureLoader().load('/img/Paper%20Game%20Image.png', (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      this.sceneTexture = tex;
      const mat = this.backdrop.material as THREE.MeshBasicMaterial;
      mat.map = tex;
      mat.needsUpdate = true;
    });

    // Puppeteer figure — a flat mesh sitting between the backdrop (z=-1)
    // and the cast (z=-0.4), so the puppeteer is "in the background" but
    // in front of the painted scene. Stays on layer 0 (background) so
    // it renders to #stage-bg and ends up BEHIND the floor HTML overlay.
    const puppeteerTex = new THREE.TextureLoader().load('/img/Pupeteer No Background.png');
    puppeteerTex.colorSpace = THREE.SRGBColorSpace;
    // Source image is 1263 × 832 — preserve that aspect ratio (≈ 1.518:1).
    const PUPPET_W = 6;
    const PUPPET_H = PUPPET_W * (832 / 1263);
    this.puppeteerMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(PUPPET_W, PUPPET_H),
      new THREE.MeshBasicMaterial({
        map: puppeteerTex,
        transparent: true,
        side: THREE.DoubleSide,
        // Cool blue-grey grade pushes the puppeteer toward the backdrop
        // in aerial-perspective terms — sits between cast (0x808080,
        // neutral) and the painted backdrop (0x2a3852, deep cool).
        // Reads as "further away" without leaving the cast plane.
        color: 0x586878,
      }),
    );
    // Start fully tucked below the stage. setPuppeteerVisible(true) lerps
    // the y up to PUPPETEER_HOME_Y in the per-frame update.
    this.puppeteerMesh.position.set(0, Stage.PUPPETEER_HIDDEN_Y, -0.7);
    this.puppeteerMesh.visible = false;
    this.scene.add(this.puppeteerMesh);

    // Animated curtain — find the <img id="curtain-frame"> overlay and
    // preload all 121 frames. Curtain starts at frame 61 (closed) and
    // every animation awaits curtainPreloadPromise so we never display
    // an unloaded frame mid-tween (no white flashes).
    this.curtainImgEl = document.getElementById('curtain-frame') as HTMLImageElement | null;
    const loadPromises: Promise<void>[] = [];
    for (let i = 1; i <= Stage.CURTAIN_FRAME_COUNT; i++) {
      const img = new Image();
      loadPromises.push(new Promise<void>(resolve => {
        img.onload = () => resolve();
        img.onerror = () => resolve();   // don't block on a missing frame
      }));
      img.src = Stage.curtainFrameUrl(i);
      this.curtainFramesPreloaded.push(img);
    }
    this.curtainPreloadPromise = Promise.all(loadPromises).then(() => {});
    this.curtainFrameIndex = Stage.CURTAIN_CLOSED_FRAME_1 - 1;
    if (this.curtainImgEl) {
      this.curtainImgEl.src = Stage.curtainFrameUrl(Stage.CURTAIN_CLOSED_FRAME_1);
    }

    // Preload backdrop override images upfront so a `set` mid-play
    // doesn't have to wait on a network fetch — the curtain just plays
    // its close → swap → open while the texture is already in memory.
    this.preloadBackdropOverrides();

    // Postprocessing: stylize the splat + backdrop + silhouette via the
    // paper shader on the BACKGROUND renderer (#stage-bg). The composer's
    // renderTarget needs alpha so empty pixels stay transparent — the
    // default RGBAFormat has it.
    this.composer = new EffectComposer(this.bgRenderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.paperPass = new ShaderPass(PaperShader);
    this.composer.addPass(this.paperPass);

    this.handleResize();
    window.addEventListener('resize', () => this.handleResize());

    // Click hotspots: when a choice has registered targets, raycast the click
    // against the active sprites/props. Mousemove sets the cursor over them.
    this.renderer.domElement.addEventListener('click', e => this.handleHotspotClick(e));
    this.renderer.domElement.addEventListener('mousemove', e => this.handleHotspotHover(e));
  }

  /** Register a click handler for a sprite/prop key — fires when user clicks
   *  the on-stage object. Cleared by clearHotspots(). */
  setupHotspot(targetId: string, handler: () => void) {
    this.hotspotHandlers.set(targetId, handler);
  }

  /** Drop all registered hotspots — call when a choice resolves. */
  clearHotspots() {
    this.hotspotHandlers.clear();
    this.renderer.domElement.style.cursor = 'default';
  }

  private hitFirstHotspot(e: MouseEvent): string | null {
    if (this.hotspotHandlers.size === 0) return null;
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointerVec.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointerVec.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointerVec, this.camera);
    this.raycaster.layers.set(PAPER_LAYER);
    for (const [id] of this.hotspotHandlers) {
      const target = this.sprites.get(id as VoiceKey) ?? this.props.get(id);
      if (!target || !target.mesh.visible) continue;
      const hits = this.raycaster.intersectObject(target.mesh);
      if (hits.length > 0) return id;
    }
    return null;
  }

  private handleHotspotClick(e: MouseEvent) {
    const id = this.hitFirstHotspot(e);
    if (id) this.hotspotHandlers.get(id)?.();
  }

  private handleHotspotHover(e: MouseEvent) {
    if (this.hotspotHandlers.size === 0) return;
    this.renderer.domElement.style.cursor = this.hitFirstHotspot(e) ? 'pointer' : 'default';
  }

  setView(view: View, duration = 1200) {
    // Camera stays locked in audience pose unless the user opts in via the
    // settings panel. Audio/silhouette logic still updates so the play's
    // backstage moments still feel different on the audio side.
    this.puppeteerSilhouette.visible = view === 'audience';
    if (!this.cameraCutsEnabled) return;
    if (view === this.currentView) return;
    this.fromPose = this.snapshotPose();
    this.toPose = POSES[view];
    this.viewLerp = 0;
    this.viewLerpDuration = duration;
    this.viewLerpStart = performance.now();
    this.currentView = view;
  }

  setPuppet(visible: boolean, color = 0xc8b48c) {
    this.puppetSlot.visible = visible;
    if (visible) {
      (this.puppetSlot.material as THREE.MeshStandardMaterial).color.setHex(color);
    }
  }

  /** Visually lift one mask off the shelf, matched to a voice swap. */
  liftMask(index: number) {
    const masks = this.maskShelf.children as THREE.Mesh[];
    masks.forEach((m, i) => {
      const target = i === index ? 0.35 : 0;
      m.userData.targetY = target;
    });
  }

  /**
   * Load the photoreal theater splat. Resolves when the file is fetched and
   * decoded; the splat then renders behind the proscenium frame.
   */
  loadSplat(url = SPLAT_URL): Promise<void> {
    if (this.splatReady) return this.splatReady;
    console.log('[stage] loading splat', url);
    const splat = new SplatMesh({
      url,
      onProgress: e => {
        if (e.lengthComputable) {
          console.log(`[stage] splat ${(e.loaded / e.total * 100).toFixed(0)}%`);
        }
      },
    });
    // Marble exports are already Y-up — no rotation needed. (Spark's README
    // uses quaternion(1,0,0,0) for Y-down captures; that would flip this
    // splat upside-down.)
    splat.position.copy(SPLAT_DEFAULT.position);
    splat.scale.setScalar(SPLAT_DEFAULT.scale);
    splat.frustumCulled = false;
    // Gloomy cast — splat.recolor RGB multiplies per-splat color on every
    // frame. Slightly cool desaturation at ~45% brightness reads as "low
    // candle light, abandoned space" without losing readable shapes.
    splat.recolor.setRGB(0.38, 0.38, 0.46);
    splat.visible = this.splatVisibleEnabled;
    this.scene.add(splat);
    this.splat = splat;
    this.splatReady = splat.initialized
      .then(() => {
        const bbox = splat.getBoundingBox(true);
        const size = new THREE.Vector3();
        const bboxCenter = new THREE.Vector3();
        bbox.getSize(size);
        bbox.getCenter(bboxCenter);
        const fmt = (v: THREE.Vector3) => `(${v.x.toFixed(2)}, ${v.y.toFixed(2)}, ${v.z.toFixed(2)})`;
        // Sample-compute the centroid of *visible* splats — Marble exports
        // include transparent strays outside the actual room, so the bbox
        // center is unreliable. The centroid of opaque splats is inside the
        // visible mass by definition, which is what we want as our world
        // origin reference (cameras tuned around (0,0,0) end up in the room).
        const centroid = new THREE.Vector3();
        let count = 0;
        splat.forEachSplat((index, c, _s, _q, opacity) => {
          if (index % 100 !== 0) return;
          if (opacity < 0.3) return;
          centroid.add(c);
          count++;
        });
        if (count > 0) centroid.divideScalar(count);
        else centroid.copy(bboxCenter);
        console.log(
          `[stage] splat ready — numSplats=${splat.packedSplats?.numSplats ?? '?'} ` +
          `bboxSize=${fmt(size)} bboxCenter=${fmt(bboxCenter)} centroid=${fmt(centroid)} (${count} samples)`,
        );
        // Native scale, no rotation. Pin the centroid to world origin, then
        // shift toward the audience camera (at z=4.2) so the splat's stage
        // sits closer to where the puppet action is.
        splat.scale.setScalar(1.0);
        splat.position.copy(centroid).negate();
        splat.position.z += 1.5;
        splat.updateMatrixWorld(true);
        const worldBox = bbox.clone().applyMatrix4(splat.matrixWorld);
        console.log(
          `[stage] splat placed — scale=1.0 position=${fmt(splat.position)} ` +
          `worldMin=${fmt(worldBox.min)} worldMax=${fmt(worldBox.max)}`,
        );
      })
      .catch(err => {
        console.warn('[stage] splat failed to load — continuing without it', err);
      });
    return this.splatReady;
  }

  /** Tune the splat transform live (call from devtools: stage.setSplatTransform(...)). */
  setSplatTransform(opts: { x?: number; y?: number; z?: number; rotX?: number; rotY?: number; rotZ?: number; scale?: number }) {
    if (!this.splat) return;
    if (opts.x !== undefined) this.splat.position.x = opts.x;
    if (opts.y !== undefined) this.splat.position.y = opts.y;
    if (opts.z !== undefined) this.splat.position.z = opts.z;
    if (opts.rotX !== undefined) this.splat.rotation.x = opts.rotX;
    if (opts.rotY !== undefined) this.splat.rotation.y = opts.rotY;
    if (opts.rotZ !== undefined) this.splat.rotation.z = opts.rotZ;
    if (opts.scale !== undefined) this.splat.scale.setScalar(opts.scale);
  }

  /** Toggle just the proscenium frame (curtains + valance). Hidden by default. */
  setProsceniumVisible(visible: boolean) {
    this.proscenium.visible = visible;
  }
  toggleProscenium() {
    this.proscenium.visible = !this.proscenium.visible;
    return this.proscenium.visible;
  }

  /** Whether the splat auto-rotates around its Y axis each frame. Off by default. */
  setRotateSplat(enabled: boolean) {
    this.rotateSplatEnabled = enabled;
  }

  /** Whether scripted scene cuts (audience ↔ backstage) move the camera.
   *  Off by default — camera locks to audience pose for the duration of the play. */
  setCameraCuts(enabled: boolean) {
    this.cameraCutsEnabled = enabled;
  }

  /** Get a paper-character sprite by voice key. */
  sprite(voice: VoiceKey): PaperSprite | undefined {
    return this.sprites.get(voice);
  }

  /** Get a scene-element prop sprite by key. */
  prop(key: string): PaperSprite | undefined {
    return this.props.get(key);
  }

  /** Show / hide the mockup mask shelf at the back of the stage. */
  setMasksVisible(visible: boolean) {
    this.maskShelf.visible = visible;
  }

  /** Show / hide the textured puppeteer figure that sits in front of
   *  the painted backdrop. Game toggles this on while a voice_prompt
   *  or conversation beat is awaiting the player's voice input. The
   *  mesh slides UP from below the stage on show and DOWN on hide
   *  (animated in update()) — never just pops in or out. */
  setPuppeteerVisible(visible: boolean) {
    this.puppeteerTargetY = visible
      ? Stage.PUPPETEER_HOME_Y
      : Stage.PUPPETEER_HIDDEN_Y;
    // Make sure the mesh is rendering during the slide. The hide path
    // sets visible=false later, once the mesh has fully tucked away.
    if (visible) this.puppeteerMesh.visible = true;
  }

  /** Re-render every sprite's canvas texture. Used when fxSettings.showLabels
   *  flips so the debug labels appear / disappear without reconstruction. */
  repaintAllSprites() {
    this.sprites.forEach(s => s.repaint());
    this.props.forEach(p => p.repaint());
  }

  /** Hide every character and prop, resetting them to their home
   *  positions. Used by the `place` beat to clear the stage before
   *  populating a new scene's initial cast/props. */
  clearStage() {
    this.sprites.forEach(s => s.hide());
    this.props.forEach(p => p.hide());
  }

  /** Project a sprite's world position to normalized screen X (0..1).
   *  Returns null if the sprite isn't on stage or projects offscreen. The
   *  spotlight uses this to follow whoever is currently speaking. */
  getSpriteScreenX(voice: VoiceKey): number | null {
    const sprite = this.sprites.get(voice);
    if (!sprite || !sprite.mesh.visible) return null;
    const v = sprite.mesh.getWorldPosition(new THREE.Vector3());
    v.project(this.camera); // → NDC [-1, 1]
    return (v.x + 1) / 2;
  }

  /** Same as getPropScreenRect but for character sprites (keyed by
   *  VoiceKey). Used by the parser-CYOA hotspot overlay so an NPC sprite
   *  can be clicked the same way an interactable object can. */
  getCharacterScreenRect(voice: VoiceKey): { x: number; y: number; w: number; h: number } | null {
    const sprite = this.sprites.get(voice);
    if (!sprite || !sprite.mesh.visible) return null;
    // Prefer the alpha-tight content box so the hotspot hugs the
    // visible silhouette rather than the full quad's letterboxed
    // bounds. Falls back to the mesh AABB if the canvas hasn't
    // produced content yet (image still decoding).
    const tight = sprite.getWorldContentBox();
    return tight
      ? this.projectBoxToScreenRect(tight)
      : this.projectMeshToScreenRect(sprite.mesh);
  }

  /** Project a prop's bounding box to viewport-pixel screen rect. Used by
   *  the parser-CYOA hotspot overlay so HTML clickables can be pinned over
   *  on-stage interactable objects. Returns null when the prop isn't
   *  visible / not loaded. */
  getPropScreenRect(key: string): { x: number; y: number; w: number; h: number } | null {
    const sprite = this.props.get(key);
    if (!sprite || !sprite.mesh.visible) return null;
    const tight = sprite.getWorldContentBox();
    return tight
      ? this.projectBoxToScreenRect(tight)
      : this.projectMeshToScreenRect(sprite.mesh);
  }

  /** Shared projection: walk an AABB's 8 corners through the camera and
   *  return the tightest screen-aligned pixel rect. Skipped when the
   *  bounds are degenerate. */
  private projectMeshToScreenRect(mesh: THREE.Object3D): { x: number; y: number; w: number; h: number } | null {
    const box = new THREE.Box3().setFromObject(mesh);
    return this.projectBoxToScreenRect(box);
  }

  /** Same projection but takes a world-space Box3 directly — used by
   *  the alpha-tight content box helper so hotspot rects can hug the
   *  visible silhouette instead of the full quad. */
  private projectBoxToScreenRect(box: THREE.Box3): { x: number; y: number; w: number; h: number } | null {
    if (!isFinite(box.min.x) || !isFinite(box.max.x)) return null;
    const W = window.innerWidth;
    const H = window.innerHeight;
    let minSX = Infinity, minSY = Infinity, maxSX = -Infinity, maxSY = -Infinity;
    const v = new THREE.Vector3();
    for (const x of [box.min.x, box.max.x]) {
      for (const y of [box.min.y, box.max.y]) {
        for (const z of [box.min.z, box.max.z]) {
          v.set(x, y, z).project(this.camera);
          const sx = (v.x * 0.5 + 0.5) * W;
          const sy = (1 - (v.y * 0.5 + 0.5)) * H;
          if (sx < minSX) minSX = sx;
          if (sx > maxSX) maxSX = sx;
          if (sy < minSY) minSY = sy;
          if (sy > maxSY) maxSY = sy;
        }
      }
    }
    const w = maxSX - minSX;
    const h = maxSY - minSY;
    if (w <= 0 || h <= 0) return null;
    return { x: minSX, y: minSY, w, h };
  }

  /** Per-key image overrides. If a `set` beat names one of these keys,
   *  the backdrop loads the listed image instead of the universal
   *  scene.webp. All overrides are preloaded in the constructor so the
   *  scene-change curtain animation never has to wait on a fetch. */
  private static readonly BACKDROP_OVERRIDES: Record<string, string> = {
    river: '/img/riverbg.png',
    swamp: '/img/swamp_bg.png',
    witchHouse: '/img/house_bg.png',
    woods: '/img/forest_bg.png',
    crossroads: '/img/crossroads_bg.png',
    mountain: '/img/mountains_bg.png',
    // finale_warm keeps the original scene.webp; finale_cold (the
    // parser-CYOA hollow ending) now uses the dedicated finale art.
    finale_warm: '/img/scene.webp',
    finale_cold: '/img/finale_bg.png',
  };
  private backdropOverrideTextures = new Map<string, THREE.Texture>();

  private preloadBackdropOverrides() {
    const loader = new THREE.TextureLoader();
    for (const [key, url] of Object.entries(Stage.BACKDROP_OVERRIDES)) {
      const tex = loader.load(
        url,
        undefined,
        undefined,
        () => {
          // 404 / decode failure — drop this entry so setBackdrop()
          // falls through to the procedural backdrop instead of
          // rendering a blank texture for a missing file.
          this.backdropOverrideTextures.delete(key);
          console.warn(`[stage] backdrop override missing, falling back: ${url}`);
        },
      );
      tex.colorSpace = THREE.SRGBColorSpace;
      this.backdropOverrideTextures.set(key, tex);
    }
  }

  /** Swap the painted backdrop (instant). Use behind closed curtains for a
   *  hidden scene change. Per-key override images take precedence over the
   *  universal scene.webp; otherwise that texture (or a placeholder) is
   *  used for every key. */
  setBackdrop(key: string) {
    const mat = this.backdrop.material as THREE.MeshBasicMaterial;

    const overrideTex = this.backdropOverrideTextures.get(key);
    if (overrideTex) {
      mat.map = overrideTex;
      mat.needsUpdate = true;
      return;
    }

    if (this.sceneTexture) {
      mat.map = this.sceneTexture;
    } else {
      let tex = this.backdropTextures.get(key);
      if (!tex) {
        tex = makeBackdropTexture(key);
        this.backdropTextures.set(key, tex);
      }
      mat.map = tex;
    }
    mat.needsUpdate = true;
  }

  /** Live curtain state. Set immediately when an open/close animation
   *  starts so subsequent beats (e.g. enter/prop) can decide whether to
   *  slide a sprite in from a side or just snap it to its home position
   *  hidden behind the closed curtain. */
  private curtainIsOpen = false;
  isCurtainOpen(): boolean { return this.curtainIsOpen; }

  /** Animate forward to the closed midpoint (frame 61). Awaits the
   *  preload promise so we never tween through unloaded frames. */
  curtainClose(): Promise<void> {
    this.curtainIsOpen = false;
    return this.curtainPreloadPromise.then(() =>
      this.animateCurtainTo(Stage.CURTAIN_CLOSED_FRAME_1 - 1));
  }

  /** Animate forward to the second open boundary (frame 121), then
   *  invisibly snap back to frame 1 — both render as "fully open" so
   *  the snap is undetectable. This way the next curtainClose() can
   *  animate forward 1 → 61 (the closing half of the loop). */
  curtainOpen(): Promise<void> {
    this.curtainIsOpen = true;
    return this.curtainPreloadPromise.then(async () => {
      await this.animateCurtainTo(Stage.CURTAIN_OPEN_END_1 - 1);
      this.curtainFrameIndex = Stage.CURTAIN_OPEN_START_1 - 1;
      if (this.curtainImgEl) {
        this.curtainImgEl.src = Stage.curtainFrameUrl(Stage.CURTAIN_OPEN_START_1);
      }
    });
  }

  private animateCurtainTo(targetIndex: number): Promise<void> {
    if (!this.curtainImgEl) return Promise.resolve();
    const start = this.curtainFrameIndex;
    if (start === targetIndex) return Promise.resolve();
    const dur = Stage.CURTAIN_DURATION_MS;
    const startedAt = performance.now();
    return new Promise(resolve => {
      const tick = () => {
        const t = Math.min(1, (performance.now() - startedAt) / dur);
        // Linear sample along the frame range.
        const idx = Math.round(start + (targetIndex - start) * t);
        if (idx !== this.curtainFrameIndex) {
          this.curtainFrameIndex = idx;
          if (this.curtainImgEl) {
            this.curtainImgEl.src = Stage.curtainFrameUrl(idx + 1);
          }
        }
        if (t >= 1) { resolve(); return; }
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  private static curtainFrameUrl(n: number): string {
    return `/sprites/b_papercraft_video_ani/frames_nobg/frame_${String(n).padStart(4, '0')}.png`;
  }

  /** Toggle the watercolor / paper-grain stylization pass over the splat. */
  setPaperEffect(enabled: boolean) {
    this.paperPass.enabled = enabled;
  }

  /** Show or hide the gaussian splat environment. Stored as a flag so the
   *  setting applies even if the splat hasn't finished loading yet. */
  setSplatVisible(visible: boolean) {
    this.splatVisibleEnabled = visible;
    if (this.splat) this.splat.visible = visible;
  }

  pulseSpotlight() {
    this.spotlight.intensity = 4.5;
  }

  start() {
    const tick = () => {
      requestAnimationFrame(tick);
      this.update();
      // Pass 1: BACKGROUND — splat + backdrop + silhouette through the
      // stylization composer, written to the bgRenderer's canvas
      // (#stage-bg, z-1 — behind the floor HTML overlay).
      this.camera.layers.set(0);
      this.composer.render();
      // Pass 2: FOREGROUND — paper props (sprites, props, proscenium,
      // masks, puppet slot) on the main renderer's canvas (#stage,
      // z-3 — in front of the floor). Unstyled, transparent clear.
      this.camera.layers.set(PAPER_LAYER);
      this.renderer.render(this.scene, this.camera);
      this.camera.layers.set(0);
    };
    tick();
  }

  private update() {
    const now = performance.now();
    const dt = Math.min(0.05, (now - this.lastFrameTime) / 1000);
    this.lastFrameTime = now;

    // Splat auto-rotate (settings toggle, off by default).
    if (this.rotateSplatEnabled && this.splat) {
      this.splat.rotateY(0.25 * dt);
    }

    // Strip last frame's parallax so the view lerp / pose snapshot sees an
    // unbiased base camera position. We re-apply the new parallax below.
    this.camera.position.x -= this.parallaxAppliedX;
    this.camera.position.y -= this.parallaxAppliedY;

    // Camera lerp.
    if (this.viewLerp < 1) {
      const t = Math.min(1, (now - this.viewLerpStart) / this.viewLerpDuration);
      const e = easeInOut(t);
      const pos = new THREE.Vector3().lerpVectors(this.fromPose.pos, this.toPose.pos, e);
      const look = new THREE.Vector3().lerpVectors(this.fromPose.look, this.toPose.look, e);
      this.camera.position.copy(pos);
      this.camera.lookAt(look);
      this.currentLook.copy(look);
      this.camera.fov = lerp(this.fromPose.fov, this.toPose.fov, e);
      this.camera.updateProjectionMatrix();
      this.viewLerp = t;
    }

    // Spotlight settle.
    this.spotlight.intensity += (2.4 - this.spotlight.intensity) * 0.04;

    // Mask lift tween.
    (this.maskShelf.children as THREE.Mesh[]).forEach(m => {
      const target = (m.userData.targetY as number) ?? 0;
      m.position.y += (target - m.position.y) * 0.12;
    });

    // Paper character sprites.
    this.sprites.forEach(s => s.update(dt));
    // Scene-element prop sprites.
    this.props.forEach(p => p.update(dt));
    // Curtain animation is image-based now (driven by curtainOpen/Close).

    // Puppeteer slide-in / slide-out. Exponential lerp toward the
    // target Y. When fully tucked away (target = HIDDEN_Y and we've
    // arrived), set mesh.visible = false to skip the GPU draw.
    {
      const m = this.puppeteerMesh;
      const target = this.puppeteerTargetY;
      const lerp = Math.min(1, 4.0 * dt);
      m.position.y += (target - m.position.y) * lerp;
      if (target === Stage.PUPPETEER_HIDDEN_Y &&
          Math.abs(m.position.y - Stage.PUPPETEER_HIDDEN_Y) < 0.05) {
        m.visible = false;
      }
    }

    // Re-apply parallax on top of the unbiased camera. When the setting is
    // off, we lerp the offset back toward zero so disabling feels gradual.
    const aimX = fxSettings.parallax ? this.parallaxTargetX : 0;
    const aimY = fxSettings.parallax ? this.parallaxTargetY : 0;
    this.parallaxAppliedX += (aimX - this.parallaxAppliedX) * 0.06;
    this.parallaxAppliedY += (aimY - this.parallaxAppliedY) * 0.06;
    this.camera.position.x += this.parallaxAppliedX;
    this.camera.position.y += this.parallaxAppliedY;
  }

  /** Set the camera-parallax target from a normalized mouse position
   *  (-1..1 on each axis, 0 at viewport center). main.ts calls this on
   *  mousemove. The closer scene layers shift more than far ones because
   *  the camera frustum slides without re-aiming. */
  setMouseParallaxTarget(nx: number, ny: number) {
    const AMOUNT_X = 0.28;   // world units of camera shift at full deflection
    const AMOUNT_Y = 0.10;
    this.parallaxTargetX = Math.max(-1, Math.min(1, nx)) * AMOUNT_X;
    this.parallaxTargetY = Math.max(-1, Math.min(1, ny)) * AMOUNT_Y;
  }

  private snapshotPose(): CameraPose {
    return {
      pos: this.camera.position.clone(),
      look: this.currentLook.clone(),
      fov: this.camera.fov,
    };
  }

  private applyPose(p: CameraPose) {
    this.camera.position.copy(p.pos);
    this.camera.lookAt(p.look);
    this.currentLook.copy(p.look);
    this.camera.fov = p.fov;
    this.camera.updateProjectionMatrix();
  }

  private handleResize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.bgRenderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    if (this.composer) {
      this.composer.setSize(w, h);
      (this.paperPass.uniforms.resolution.value as THREE.Vector2).set(w, h);
    }
  }
}

function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }
function easeInOut(t: number) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }

/** Per-set palette + label for the placeholder backdrops. Replace with real
 *  painted PNGs by writing a different texture loader. */
const BACKDROP_PALETTES: Record<string, { color: number; label: string }> = {
  theater:      { color: 0x2a1a14, label: 'THEATER' },
  woods:        { color: 0x132014, label: 'WOODS' },
  clearing:     { color: 0x1a1830, label: 'CLEARING' },
  finale_warm:  { color: 0x3a2820, label: 'GENTLE END' },
  finale_cold:  { color: 0x14202a, label: 'BITTER END' },
};

function makeBackdropTexture(key: string): THREE.CanvasTexture {
  const palette = BACKDROP_PALETTES[key] ?? BACKDROP_PALETTES.theater;
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 512;
  const ctx = canvas.getContext('2d')!;
  const baseHex = `#${palette.color.toString(16).padStart(6, '0')}`;
  const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
  grad.addColorStop(0, baseHex);
  grad.addColorStop(1, '#000');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = 'rgba(255,255,255,0.07)';
  ctx.lineWidth = 6;
  ctx.strokeRect(8, 8, canvas.width - 16, canvas.height - 16);
  ctx.fillStyle = 'rgba(255,255,255,0.18)';
  ctx.font = 'bold 96px ui-monospace, monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(palette.label, canvas.width / 2, canvas.height / 2);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
